import * as R from 'ramda'
import * as React from 'react'
import PropTypes from 'prop-types'

import * as common from '@rushplay/common'
import css from '@styled-system/css'
import styled from '@emotion/styled'

import * as ServerConfiguration from './server-configuration'
import * as Constants from './constants'
import * as icons from './icons'

function borderColor(props) {
  if (props.focused) {
    return 'ebony-clay'
  }

  if (props.valid) {
    return 'success'
  } else if (props.invalid) {
    return 'danger'
  } else {
    return 'ebony-clay'
  }
}

function hover(props) {
  if (props.valid || props.invalid || props.disabled || props.focused) {
    return null
  } else {
    return 'inputHover'
  }
}

const Wrapper = styled.label`
  ${props =>
    css({
      alignItems: 'center',
      display: 'flex',
      color: props.focused ? 'secondary' : '#f3e3d8',
      backgroundColor: props.dark ? '#0C132B' : '#151E2D',
      border: '1px solid',
      borderColor: borderColor(props),
      borderRadius: 0,
      boxShadow: props.focused ? 0 : null,
      fontSize: '16px',
      flexGrow: 1,
      flexShrink: 1,
      overflow: 'hidden',

      py: props.limitInput ? '0px' : '0.35em',
      '&:hover': {
        borderColor: hover(props),
      },
    })}

  &:disabled {
    cursor: not-allowed;
  }
`

const IconWrapper = styled.div`
  ${props => props.onClick && 'cursor: pointer'};
  ${props =>
    css({
      color: props.color,
    })}
  display: inline-flex;
  justify-content: center;
  ${props => (props.limitInput ? 'width: 3.5em' : 'width: 2.5em')};
  ${props => props.limitInput && 'background-color: #1849DC'};
  ${props => props.limitInput && 'height: 45px'};
  align-items: center;
  padding-right: 1px;
`

const InputField = styled('input', {
  shouldForwardProp: common.noneOf(['prependIcon', 'hasStatus']),
})`
  ${props =>
    css({
      width: '100%',
      flexGrow: 1,
      flexShrink: 1,
      fontFamily: 'body',
      fontSize: '16px',
      paddingTop: '0.2em',
      paddingBottom: '0.2em',
      paddingLeft: props.prependIcon ? '.5em' : '1em',
      paddingRight: props.hasStatus ? '0em' : '2em',
      color: 'white',
      backgroundColor: props.dark ? '#0C132B' : 'transparent',
      border: 0,
      '&::placeholder': {
        color: 'ebony-clay',
        fontSize: 2,
      },
      '&:focus': { outline: 'none' },
    })}

  &:disabled {
    cursor: not-allowed;
  }

  // hack to change the background-color that's added by autofill
  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus,
  :-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
    -webkit-text-fill-color: #fff !important;
    -webkit-box-shadow: 0 0 0 50px #0c101c inset;
  }
`

function getCurrencySymbol(configuration, currencyCode) {
  if (configuration.currencies?.length > 0) {
    const currency = configuration.currencies.find(
      currency => currency.code === currencyCode
    )
    return currency?.symbol
  }

  return configuration.currency.symbol || '€'
}

export function Input(props) {
  const inputRef = React.useRef(null)
  const [focused, setFocused] = React.useState(props.autoFocus)
  const configuration = ServerConfiguration.useServerConfiguration()
  const value = props.value && props.value.toString()
  const valid = props.valid && props.visited && !props.suppressVisualFeedback
  const invalid =
    props.invalid &&
    !props.valid &&
    props.visited &&
    !props.suppressVisualFeedback

  React.useEffect(() => {
    // Handle focus state for wrapper styles
    if (focused && inputRef.current) {
      inputRef.current.focus()
    }
  }, [focused, inputRef])

  function handleBlur() {
    setFocused(false)
    props.onBlur()
  }

  return (
    <Wrapper
      limitInput={props.limitInput}
      empty={R.isEmpty(props.value)}
      valid={valid}
      invalid={invalid}
      focused={focused}
      disabled={props.disabled}
      ref={inputRef}
      dark={props.dark}
    >
      {props.prependIcon && (
        <IconWrapper color="input">{props.prependIcon}</IconWrapper>
      )}
      {props.currency && (
        <IconWrapper color="input" limitInput={props.limitInput}>
          {getCurrencySymbol(configuration, props.currency)}
        </IconWrapper>
      )}
      <InputField
        autoComplete={props.autoComplete}
        autoCorrect={props.autoCorrect}
        autoFocus={props.autoFocus}
        disabled={props.disabled}
        id={props.id}
        inputMode={props.inputMode}
        maxLength={props.maxLength}
        minLength={props.minLength}
        prependIcon={props.prependIcon || props.currency}
        placeholder={props.placeholder}
        type={props.type}
        value={value}
        hasStatus={valid || invalid}
        data-testid={`${props.id}.input-field`}
        onChange={props.onChange}
        onBlur={handleBlur}
        onFocus={() => setFocused(true)}
        dark={props.dark}
      />
      {props.appendIcon && (
        <IconWrapper color="ebony-clay" onClick={props.onClick}>
          <props.appendIcon />
        </IconWrapper>
      )}
      {valid && (
        <IconWrapper color="success">
          <icons.CheckCircle />
        </IconWrapper>
      )}
      {invalid && (
        <IconWrapper color="danger">
          <icons.Error />
        </IconWrapper>
      )}
    </Wrapper>
  )
}

Input.propTypes = {
  appendIcon: PropTypes.func,
  autoComplete: PropTypes.string,
  autoCorrect: PropTypes.oneOf(['on', 'off']),
  autoFocus: PropTypes.bool,
  // TODO: create amountInput that passes correct icon instead of relying on currency
  currency: PropTypes.oneOf(Constants.CurrencyCodes),
  dark: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  inputMode: PropTypes.oneOf(['numeric', 'email', 'decimal', 'tel']),
  invalid: PropTypes.bool,
  maxLength: PropTypes.string,
  minLength: PropTypes.string,
  name: PropTypes.string,
  limitInput: PropTypes.bool,
  prependIcon: PropTypes.element,
  placeholder: PropTypes.string,
  suppressVisualFeedback: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visited: PropTypes.bool,
  valid: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
}
